import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../client";
import { useAuthStore } from "@/modules/auth";

export interface AttachmentMeta {
  id: string;
  purpose: string;
  parentType: string;
  parentId: string | null;
  filename: string;
  contentType: string;
  sizeBytes: number;
  caption: string | null;
  supportingText: string;
  uploadedById: string;
  uploadedByName?: string | null;
  uploadedAt: string;
  status: "active" | "inactive" | "quarantined";
  docNo: string | null;
  docSerial: number | null;
  docFy: string | null;
  workUnitId?: string | null;
  workUnitCtrlNo?: string | null;
  projectId?: string | null;
  projectName?: string | null;
  clientId?: string | null;
  clientName?: string | null;
}

export function useUploadAttachment(invalidateKeys?: string[][]) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (formData: FormData) =>
      api
        .post<AttachmentMeta>("/attachments", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((r) => r.data),
    onSuccess: () => {
      if (invalidateKeys) {
        invalidateKeys.forEach((key) => qc.invalidateQueries({ queryKey: key }));
      }
    },
  });
}

export function useDeleteAttachment(invalidateKeys?: string[][]) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/attachments/${id}`).then(() => true),
    onSuccess: () => {
      invalidateKeys?.forEach((key) => qc.invalidateQueries({ queryKey: key }));
    },
  });
}

export function useWorkUnitBucket(wuId: string | null) {
  return useQuery({
    queryKey: ["wu-bucket", wuId],
    enabled: !!wuId,
    queryFn: () =>
      api
        .get<{ results: AttachmentMeta[] }>(`/work-units/${wuId}/attachments`)
        .then((r) => r.data.results),
  });
}

export interface DocumentRegistryResponse {
  results: AttachmentMeta[];
  total: number;
}

export function useDocumentRegistry(params: {
  search?: string;
  purpose?: string;
  fy?: string;
}) {
  return useQuery({
    queryKey: ["documents", params],
    queryFn: () =>
      api
        .get<DocumentRegistryResponse>("/documents", { params })
        .then((r) => r.data),
  });
}

export function attachmentDownloadUrl(id: string): string {
  const base = api.defaults.baseURL ?? "";
  const token = useAuthStore.getState().accessToken;
  const qs = token ? `?token=${encodeURIComponent(token)}` : "";
  return `${base}/attachments/${id}/download${qs}`;
}

export function attachmentPreviewUrl(id: string): string {
  const base = api.defaults.baseURL ?? "";
  const token = useAuthStore.getState().accessToken;
  const qs = token ? `?token=${encodeURIComponent(token)}` : "";
  return `${base}/attachments/${id}/preview${qs}`;
}

// Strict whitelist mirrors server-side PREVIEWABLE_CONTENT_TYPES.
const PREVIEWABLE_CONTENT_TYPES = new Set<string>([
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/gif",
  "image/webp",
  "text/plain",
  "text/markdown",
  "text/x-markdown",
]);

// Extensions the server will serve inline as text/plain even when the stored
// MIME is missing/octet-stream — must stay in sync with _TEXT_INLINE_EXTENSIONS
// on the server.
const PREVIEWABLE_EXTENSIONS = [".md", ".markdown", ".txt"];

export function isPreviewable(
  contentType: string | null | undefined,
  filename?: string | null,
): boolean {
  if (contentType && PREVIEWABLE_CONTENT_TYPES.has(contentType.toLowerCase())) {
    return true;
  }
  if (filename) {
    const lower = filename.toLowerCase();
    if (PREVIEWABLE_EXTENSIONS.some((ext) => lower.endsWith(ext))) return true;
  }
  return false;
}

/**
 * Open an attachment in the best way for its content type. PDFs + images +
 * plain text + markdown open inline in a new tab; everything else triggers a
 * download. `filename` is used as a fallback when the stored MIME is missing
 * (e.g. .md uploaded as application/octet-stream).
 */
export function openAttachment(
  id: string,
  contentType: string | null | undefined,
  filename?: string | null,
): void {
  if (isPreviewable(contentType, filename)) {
    window.open(attachmentPreviewUrl(id), "_blank", "noopener,noreferrer");
  } else {
    window.location.href = attachmentDownloadUrl(id);
  }
}
